import { getTokens } from '@rsos/sinatra';
import { getAPIHost } from '@rsos/utils/metaTags';

const CLASSIFY_PATH = '/v1/harmony-chat-proxy/classify';

export interface ClassifierChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ClassifierChatOptions {
  /** Hard ceiling on round-trip; classifiers should fall back, not block. */
  timeoutMs?: number;
  temperature?: number;
  maxTokens?: number;
  /** Force strict-JSON output (no ```json fences). Default true. */
  jsonMode?: boolean;
}

/**
 * Combine the caller's abort signal with a timeout. `AbortSignal.timeout` /
 * `AbortSignal.any` are widely supported in browsers but absent in some
 * runtimes (older jsdom/node), so degrade gracefully to the caller's signal
 * rather than throwing — the server-side request still has its own timeout.
 */
function withTimeout(signal: AbortSignal, timeoutMs: number): AbortSignal {
  if (
    typeof AbortSignal.timeout !== 'function' ||
    typeof AbortSignal.any !== 'function'
  ) {
    return signal;
  }
  return AbortSignal.any([signal, AbortSignal.timeout(timeoutMs)]);
}

/**
 * Run a one-shot classification chat through the server-side proxy. Returns
 * the model's reply content, or `null` on any failure mode (no auth token,
 * non-2xx, abort, timeout, malformed body) so callers can fall back to
 * heuristics with a single branch.
 */
export async function callClassifierProxy(
  messages: ClassifierChatMessage[],
  signal: AbortSignal,
  options: ClassifierChatOptions = {},
): Promise<string | null> {
  const token = getTokens()?.token;
  if (!token) return null;

  const {
    timeoutMs = 4000,
    temperature = 0,
    maxTokens = 60,
    jsonMode = true,
  } = options;

  try {
    const resp = await fetch(`${getAPIHost()}${CLASSIFY_PATH}`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        messages,
        temperature,
        max_tokens: maxTokens,
        json_mode: jsonMode,
      }),
      signal: withTimeout(signal, timeoutMs),
    });
    if (!resp.ok) return null;

    const data = await resp.json();
    const content: unknown = data?.content;
    return typeof content === 'string' ? content : null;
  } catch {
    return null;
  }
}
